import { useMemo, useState, type ReactNode } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Divider, Select, Space, Typography } from 'antd';

export interface OpcaoSimples {
  id: string;
  name: string;
}

export interface SearchBoxCriavelProps {
  value?: string | null;
  onChange?: (id: string | undefined) => void;
  /**
   * O `Form.Item` injeta o `id` no filho para amarrar o rótulo ao campo. Sem
   * repassá-lo, clicar em "Banca" não foca a caixa e o leitor de tela não sabe
   * o nome do campo.
   */
  id?: string;
  /** Chave da consulta; o termo digitado é acrescentado automaticamente. */
  queryKey: readonly unknown[];
  buscar: (termo: string | undefined) => Promise<OpcaoSimples[]>;
  /** Ausente quando o registro não pode ser criado dali (só busca). */
  criar?: (nome: string) => Promise<OpcaoSimples>;
  placeholder: string;
  /** Nome do que se cadastra, para o texto do botão: "Cadastrar banca". */
  rotulo: string;
  /**
   * Nome do registro já escolhido, quando quem chama já o conhece. A busca
   * devolve só uma página de resultados; sem isso, um registro fora dessa
   * página apareceria como UUID na tela.
   */
  rotuloAtual?: string | null;
  /** Ação extra no rodapé, como "+Cadastrar prova" levando à tela de cadastro. */
  acaoExtra?: ReactNode;
  disabled?: boolean;
  allowClear?: boolean;
}

/**
 * Caixa de busca com cadastro embutido.
 *
 * O escopo é explícito: ao procurar banca, cartório, disciplina, assunto ou
 * delimitação, o administrador pode criar o registro dentro da própria caixa
 * caso não encontre o correto. O ponto é não interromper o cadastro em
 * andamento para ir a outra tela e voltar.
 *
 * O backend devolve o registro existente quando o nome se repete, então clicar
 * em "Cadastrar" duas vezes não cria duplicata.
 */
export function SearchBoxCriavel({
  value,
  onChange,
  id,
  queryKey,
  buscar,
  criar,
  placeholder,
  rotulo,
  rotuloAtual,
  acaoExtra,
  disabled = false,
  allowClear = true,
}: SearchBoxCriavelProps) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState(false);

  const opcoes = useQuery({
    queryKey: [...queryKey, termo],
    queryFn: () => buscar(termo || undefined),
    enabled: !disabled,
  });

  // Nomes já vistos nesta tela. Sem isso, digitar uma nova busca com um item já
  // escolhido faria a caixa mostrar o UUID: o item some da lista de resultados
  // e o Select perde o rótulo.
  const [conhecidos, setConhecidos] = useState<Record<string, string>>({});

  const cadastrar = useMutation({
    mutationFn: (nome: string) => criar!(nome),
    onSuccess: async (criado) => {
      setConhecidos((atuais) => ({ ...atuais, [criado.id]: criado.name }));
      onChange?.(criado.id);
      // Sem fechar e limpar a busca, a caixa seguiria mostrando o que foi
      // digitado e pareceria que nada foi selecionado.
      setTermo('');
      setAberto(false);
      await queryClient.invalidateQueries({ queryKey });
      message.success(`${rotulo} cadastrado.`);
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const encontradas = useMemo(() => opcoes.data ?? [], [opcoes.data]);

  const lista = useMemo(() => {
    if (!value || encontradas.some((item) => item.id === value)) return encontradas;

    const nome = rotuloAtual ?? conhecidos[value];
    return nome ? [{ id: value, name: nome }, ...encontradas] : encontradas;
  }, [encontradas, value, rotuloAtual, conhecidos]);

  const jaExiste = encontradas.some(
    (item) => item.name.toLowerCase() === termo.trim().toLowerCase(),
  );
  const podeCriar = Boolean(criar) && termo.trim().length > 0 && !jaExiste;

  return (
    <Select<string>
      id={id}
      showSearch
      allowClear={allowClear}
      disabled={disabled}
      value={value ?? undefined}
      placeholder={placeholder}
      // A busca acontece no servidor: filtrar de novo no navegador esconderia
      // resultados que vieram de páginas seguintes.
      filterOption={false}
      // A busca é controlada para podermos limpá-la depois do cadastro inline.
      searchValue={termo}
      onSearch={setTermo}
      open={aberto}
      onOpenChange={setAberto}
      onChange={(escolhido, opcao) => {
        const rotuloEscolhido = (opcao as { label?: string } | undefined)?.label;
        if (escolhido && rotuloEscolhido) {
          setConhecidos((atuais) => ({ ...atuais, [escolhido]: rotuloEscolhido }));
        }
        setTermo('');
        onChange?.(escolhido);
      }}
      loading={opcoes.isFetching}
      notFoundContent={opcoes.isFetching ? 'Buscando...' : 'Nada encontrado'}
      options={lista.map((item) => ({ value: item.id, label: item.name }))}
      style={{ width: '100%' }}
      popupRender={(menu) => (
        <>
          {menu}
          {podeCriar || acaoExtra ? <Divider style={{ margin: '4px 0' }} /> : null}
          <Space orientation="vertical" size={0} style={{ width: '100%', padding: 4 }}>
            {podeCriar ? (
              <Button
                type="text"
                block
                icon={<PlusOutlined />}
                loading={cadastrar.isPending}
                style={{ textAlign: 'left' }}
                onMouseDown={(evento) => evento.preventDefault()}
                onClick={() => cadastrar.mutate(termo.trim())}
              >
                Cadastrar {rotulo.toLowerCase()}{' '}
                <Typography.Text strong>&ldquo;{termo.trim()}&rdquo;</Typography.Text>
              </Button>
            ) : null}
            {acaoExtra}
          </Space>
        </>
      )}
    />
  );
}
